import {
  Box,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  Typography,
  TextField,
  Checkbox,
  Avatar,
  Tooltip,
  CircularProgress,
  Switch,
  ClickAwayListener,
  IconButton
} from '@material-ui/core';
import React, { useState } from 'react';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import { LRCHeader, LRCSearchHeader, validUpto, newValidity } from '../constant';
import useStyles from '../style';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import { HelpIcon, InstallerInfo, DownloadIcon } from '../Icons';
import { LRCTooltip, PriceTooltip } from '../Tooltip';
import { localeMoment as moment } from '../../common/moment';
import { isEmpty, get } from '../../../utils/lodash';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

const LightTooltip = withStyles((theme, props) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0px 1px 6px #00000029',
    fontSize: 11,
    maxWidth: props => (props.maxWidth ? props.maxWidth : 600),
    minWidth: props => (props.minWidth ? props.minWidth : 400)
  },
  arrow: { color: theme.palette.common.white, fontSize: 16 }
}))(Tooltip);

export default function LRCTable(props) {
  const {
    data,
    tier,
    loading,
    message,
    LREPlans,
    setPurchase,
    purchase,
    purchasedList,
    companyId,
    isEligibleSite,
    toggleEligibleSite,
    isMobileView,
    lrePlansBySiteId,
    getDownloadAgreement
  } = props;
  const classes = useStyles(props);
  const [ isTooltipOpen, setIsTooltipOpen ] = useState(false);
  const tierName = tier && tier.name;

  const getSearchComponent = ({ key, isCheckbox, label }) => {
    if (key) {
      let isChecked = true;
      if (data && (key === '3-YEAR-EXTN' || key === '8-YEAR-EXTN')) {
        const siteIds = [];
        data.forEach(sites => {
          const installDate = moment(sites.install_date, 'MM/DD/YYYY HH:mm A z');
          const expireDate = moment(installDate).add(2, 'y');
          const isApplicableForExtn = moment(expireDate).isSameOrAfter(moment());
          const hasNonIQTypes = checkForNonIQSites(sites);
          const isPurchased =
            purchasedList &&
            !isEmpty(purchasedList[sites.system_id]) &&
            !purchasedList[sites.system_id].isExtendedByInstaller;
          if (isApplicableForExtn && !hasNonIQTypes && !isPurchased) {
            siteIds.push(sites.system_id);
          }
        });
        if (siteIds.length > 0) {
          siteIds.forEach(site => {
            if (purchase[site] !== key) {
              isChecked = false;
              return false;
            }
          });
        } else {
          isChecked = false;
        }
      } else {
        isChecked = false;
      }
      return isCheckbox ? (
        <Box className={classes.checkboxWrapper}>
          <Checkbox
            className={classes.checkbox}
            icon={<CheckBoxOutlineBlankIcon className={classes.icon} />}
            checkedIcon={<CheckBoxOutlinedIcon className={classes.icon} />}
            checked={isChecked}
            onChange={(e, value) => toggleAllPriceDetails(key, value)}
            color="default"
          />
          <Typography className={classes.checkboxLabel} component="span">
            {label}
          </Typography>
        </Box>
      ) : (
        <TextField
          className={classes.inputField}
          InputLabelProps={{ shrink: true }}
          value=""
          // onChange={e => handleSearch(e, search.key)}
          variant="outlined"
          size="small"
        />
      );
    }
  };

  const getSiteDetails = (sku, siteId, expireDate, value, count, is90d) => {
    let label = '8 Years';
    let currentValidity = moment(expireDate).add(8, 'y');
    let periods = [];
    if (sku === '3-YEAR-EXTN') {
      label = '3 Years';
      currentValidity = moment(expireDate).add(3, 'y');
    }
    if (value) {
      periods = [
        {
          p_num: siteId,
          label,
          sku,
          qty: count,
          is90d,
          current_validity: currentValidity.format('DD-MM-YYYY')
        }
      ];
    }
    return {
      site_id: siteId,
      periods,
      company_id: companyId
    };
  };

  const toggleAllPriceDetails = (key, value) => {
    data.forEach((system, index) => {
      const installDate = moment(system.install_date, 'MM/DD/YYYY HH:mm A z');
      const expireDate = moment(installDate).add(2, 'y');
      const dateDiff = moment().diff(installDate, 'days');
      const is90d = dateDiff <= 90;
      const isApplicableForExtn = moment(expireDate).isSameOrAfter(moment());
      const hasNonIQTypes = checkForNonIQSites(system);
      const isPurchased =
        purchasedList &&
        !isEmpty(purchasedList[system.system_id]) &&
        !purchasedList[system.system_id].isExtendedByInstaller;
      if (isApplicableForExtn && !hasNonIQTypes && !isPurchased) {
        const siteDetail = getSiteDetails(key, system.system_id, expireDate, value, system.pcu_channel_count, is90d);
        setPurchase({ sku: key, siteId: system.system_id, siteDetail, value });
      }
    });
  };

  const togglePriceDetails = (sku, siteId, value, expireDate, count, is90d) => {
    const siteDetail = getSiteDetails(sku, siteId, expireDate, value, count, is90d);
    setPurchase({ sku, siteId, siteDetail, value });
  };

  const priceDetails = (price, count, siteId, sku, expireDate, isApplicableForExtn, is90d) => {
    const siteKeys = Object.keys(purchase);
    const isSiteAvailable = siteKeys.includes(siteId.toString());
    let isDisabled = false;
    let checked = false;
    if (isApplicableForExtn && isSiteAvailable) {
      isDisabled = purchase[siteId] !== sku;
      checked = purchase[siteId] === sku;
    }
    let newPrice = price;
    if (sku === '3-YEAR-EXTN') {
      const sitePlan = lrePlansBySiteId && lrePlansBySiteId.find(plan => plan.site_id.toString() === siteId.toString());
      const filteredPlan = sitePlan && sitePlan.plans && sitePlan.plans.find(site => site.discount_percentage === 100);
      newPrice = filteredPlan && filteredPlan.discount_percentage === 100 ? 0 : newPrice;
    }
    const checkboxClass = isDisabled ? clsx(classes.checkboxWrapper, classes.disableCheckbox) : classes.checkboxWrapper;
    return (
      <Box className={checkboxClass}>
        <Checkbox
          className={classes.checkbox}
          icon={<CheckBoxOutlineBlankIcon className={classes.icon} />}
          checkedIcon={<CheckBoxOutlinedIcon className={classes.icon} />}
          checked={checked}
          onChange={(e, value) => togglePriceDetails(sku, siteId, value, expireDate, count, is90d)}
          color="default"
        />
        <Typography component="div" className={classes.priceWrapper}>
          <Typography component="span" className={classes.price}>
            {`$  ${(newPrice * count).toFixed(2)}`}
          </Typography>
          <LightTooltip
            title={<PriceTooltip price={newPrice} count={count} plans={LREPlans} sku={sku} />}
            placement="bottom-end"
            arrow
          >
            <Avatar variant="square" alt="help icon" src={HelpIcon} className={classes.priceHelpIcon} />
          </LightTooltip>
        </Typography>
      </Box>
    );
  };

  const checkForNonIQSites = system => {
    if (system && system.pcu_types && system.pcu_types.length > 0) {
      let hasNonIQTypes = false;
      const pcuTypes = system.pcu_types.filter(
        value =>
          ![
            'Microinverter',
            'ACB',
            'ACM-Other',
            'ACM-ACM',
            'ACM-ACM-Test',
            'ACM-JS',
            'ACM-LG',
            'ACM-LR',
            'ACM-PNA',
            'ACM-PS',
            'ACM%',
            'IQ Relay',
            'ACM-SOL',
            'ACM-LON'
          ].includes(value)
      );
      for (let i = 0; i < pcuTypes.length; i++) {
        if (!pcuTypes[i].startsWith('IQ')) {
          hasNonIQTypes = true;
          break;
        }
      }
      return hasNonIQTypes;
    } else {
      return true;
    }
  };

  const getSystemDetails = system => {
    const installDate = moment(system.install_date, 'MM/DD/YYYY HH:mm A z');
    const firstIqInverterAt = get(system, 'first_iq_inverter_at', '')
      ? moment(system.first_iq_inverter_at, 'MM/DD/YYYY HH:mm A z')
      : '';
    let expireDate =
      get(system, 'is_lpup_system', '') && !isEmpty(firstIqInverterAt)
        ? firstIqInverterAt.add(2, 'y')
        : installDate.add(2, 'y');
    const dateDiff = moment().diff(installDate, 'days');
    let isApplicableForExtn = moment(expireDate).isSameOrAfter(moment());
    let validityDate = '--';
    let isPurchased = false;
    let sku = '';
    let installedByInstaller = false;
    const inactiveStatesList = [
      'PR' //Puerto Rico
    ];
    const inactiveState = inactiveStatesList.includes(system.state);
    let isDisabled = !isApplicableForExtn;
    if (purchasedList && !isEmpty(purchasedList[system.system_id])) {
      const details = purchasedList[system.system_id];
      installedByInstaller = details.isExtendedByInstaller;
      isApplicableForExtn = installedByInstaller;
      isPurchased = true;
      sku = details.sku;
      expireDate = installedByInstaller ? expireDate : moment(details.planEnd);
      isDisabled = false;
    }
    if (isApplicableForExtn && purchase && purchase[system.system_id]) {
      validityDate =
        purchase[system.system_id] === '3-YEAR-EXTN' ? moment(expireDate).add(3, 'y') : moment(expireDate).add(8, 'y');
      validityDate = validityDate.format('DD MMM YYYY');
    }
    const hasNonIQTypes = checkForNonIQSites(system);
    isDisabled = hasNonIQTypes || inactiveState || system.pcu_channel_count <= 0 || isDisabled;
    return {
      isApplicableForExtn,
      expireDate,
      dateDiff,
      validityDate,
      isDisabled,
      isPurchased,
      sku,
      inactiveState,
      installedByInstaller
    };
  };

  const getTooltip = index => {
    let tooltip = null;
    switch (index) {
      case 0:
        tooltip = (
          <LightTooltip
            title={<Box className={classes.tooltipText}>{validUpto}</Box>}
            placement="bottom-end"
            arrow
            maxWidth={300}
            minWidth={200}
          >
            <Avatar variant="square" alt="help icon" src={HelpIcon} className={classes.headerHelpIcon} />
          </LightTooltip>
        );
        break;
      case 1:
        tooltip = (
          <LightTooltip title={<LRCTooltip plans={LREPlans} tierName={tierName} />} placement="bottom-end" arrow>
            <Avatar variant="square" alt="help icon" src={HelpIcon} className={classes.headerHelpIcon} />
          </LightTooltip>
        );
        break;
      case 2:
        tooltip = (
          <LightTooltip
            title={<Box className={classes.tooltipText}>{newValidity}</Box>}
            placement="bottom-end"
            arrow
            maxWidth={300}
            minWidth={200}
          >
            <Avatar variant="square" alt="help icon" src={HelpIcon} className={classes.headerHelpIcon} />
          </LightTooltip>
        );
        break;
      default:
        break;
    }
    return tooltip;
  };

  return (
    <Box className={clsx(classes.root, classes.lrcRoot)}>
      <Box className={clsx(classes.switchHeaderWrapper, isMobileView && classes.mobileSwitchHeader)}>
        {!isMobileView && <Typography className={classes.tableHeading}>Labor Protection Program</Typography>}
        <Box className={classes.toogleWrapper}>
          <Typography className={clsx(classes.switchLabel, !isEligibleSite && classes.activeLabel)}>
            All Sites
          </Typography>
          <Switch
            checked={isEligibleSite}
            onChange={toggleEligibleSite}
            color="primary"
            name="checkedB"
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
          <Typography className={clsx(classes.switchLabel, isEligibleSite && classes.activeLabel)}>
            Eligible Sites
          </Typography>
          <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
            <LightTooltip
              title={
                <Box className={classes.tooltipText}>
                  Only sites with IQ series microinverters, installed in the last 2 years in a US state other than PR are eligible for Labor Protection Program extension
                </Box>
              }
              open={isTooltipOpen}
              onClick={e => setIsTooltipOpen(true)}
              onMouseEnter={e => setIsTooltipOpen(true)}
              onMouseOut={e => setIsTooltipOpen(false)}
              placement="bottom-end"
              arrow
              maxWidth={300}
              minWidth={200}
            >
              <Avatar variant="square" alt="help icon" src={HelpIcon} className={classes.eligibleHelpIcon} />
            </LightTooltip>
          </ClickAwayListener>
        </Box>
      </Box>
      <Paper className={classes.lrcTable} elevation={3}>
        <TableContainer>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {LRCHeader.map(({ column, name, hasHelpIcon, subText }, index) => {
                  return (
                    <TableCell
                      colSpan={column}
                      className={clsx(classes.header, subText && classes.subTextHeader)}
                      key={index}
                    >
                      <Typography
                        component="div"
                        className={clsx(classes.headerWrapper, subText && classes.subTextHeaderWrapper)}
                      >
                        <Typography component="div" className={classes.toolWrapper}>
                          <Typography className={classes.headerTitle}>{name}</Typography>
                          {hasHelpIcon && getTooltip(index)}
                        </Typography>
                        {subText && <Typography className={classes.subText}>{subText}</Typography>}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                {LRCSearchHeader.map((search, index) => (
                  <TableCell className={classes.header} key={index}>
                    {getSearchComponent(search, index)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                !loading &&
                data.map((system, index) => {
                  const {
                    isApplicableForExtn,
                    expireDate,
                    dateDiff,
                    validityDate,
                    isDisabled,
                    isPurchased,
                    sku,
                    inactiveState,
                    installedByInstaller
                  } = getSystemDetails(system);
                  const tableRowClass = clsx(classes.tableRow, isDisabled && classes.disableTableRow);
                  const getCellValue = plan => {
                    if (isPurchased && !installedByInstaller) {
                      if (sku === plan.sku) {
                        return (
                          <Box className={classes.purchased}>
                            <CheckCircleOutlineIcon />
                            Purchased
                          </Box>
                        );
                      } else {
                        return '--';
                      }
                    } else if (isApplicableForExtn && !isDisabled) {
                      return priceDetails(
                        dateDiff > 90 ? plan.price.discount_price_max : plan.price.discount_price_90d,
                        system.pcu_channel_count,
                        system.system_id,
                        plan.sku,
                        expireDate,
                        isApplicableForExtn,
                        dateDiff <= 90
                      );
                    } else {
                      return '--';
                    }
                  };
                  return (
                    <TableRow tabIndex={-1} key={index} className={tableRowClass}>
                      <TableCell className={clsx(classes.checkBoxTableData, classes.colorWrapper)}>
                        <Typography>{expireDate.format('DD MMM YYYY')}</Typography>
                        {!inactiveState &&
                        isPurchased &&
                        installedByInstaller && (
                          <LightTooltip
                            title={
                              <Box className={classes.tooltipText}>
                                LPP has been extended for this site by another installer
                              </Box>
                            }
                            arrow
                            maxWidth={300}
                            minWidth={200}
                          >
                            <Avatar
                              variant="square"
                              alt="installer info icon"
                              src={InstallerInfo}
                              className={classes.installerInfoIcon}
                            />
                          </LightTooltip>
                        )}
                      </TableCell>
                      {/* TODO: need to check the order of the api for 3 years and 8 years */}
                      {LREPlans &&
                        LREPlans.length !== 0 &&
                        LREPlans.map(plan => (
                          <TableCell
                            className={
                              isPurchased && !installedByInstaller ? (
                                clsx(classes.checkBoxTableData, classes.disableTableRow)
                              ) : (
                                classes.checkBoxTableData
                              )
                            }
                            key={`${index}_${plan.extension}`}
                          >
                            {getCellValue(plan)}
                          </TableCell>
                        ))}
                      {LREPlans &&
                      LREPlans.length === 0 && (
                        <TableCell className={classes.checkBoxTableData} colSpan={2}>
                          Failed to load plan info
                        </TableCell>
                      )}
                      <TableCell
                        className={clsx(
                          classes.checkBoxTableData,
                          isPurchased && !installedByInstaller && classes.disableTableRow
                        )}
                      >
                        {validityDate}
                      </TableCell>
                      <TableCell className={classes.downloadAgreement}>
                        <IconButton
                          onClick={() => getDownloadAgreement(system.system_id)}
                          className={clsx(
                            classes.downloadAgreementIcon,
                            (!isPurchased || installedByInstaller) && !isDisabled && classes.disableTableRow
                          )}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} className={classes.loader}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {data &&
              data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>No results found</TableCell>
                </TableRow>
              )}
              {message && (
                <TableRow>
                  <TableCell colSpan={4} className={classes.err}>
                    {message}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
